import { isRegenAdmin } from '../../lib/utils';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Image, Star, Clock, Trash2, Users, Settings, LogOut, FileText, Tags, Share2, UserCheck, Layers, ChevronRight, Inbox, User, Download, BarChart3, Shield, Key, Rocket, HelpCircle, ClipboardList, CalendarCheck, BookOpen, CheckSquare, Search, Cpu, FlaskConical, BellRing, Wand2, PanelLeftClose, PanelLeftOpen, Target, Trophy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { savedSearchApi, notificationApi } from '../../lib/api';

// Base navigation available to all users
const baseNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Media Library', href: '/media', icon: Image },
  { name: 'File Requests', href: '/file-requests', icon: Inbox },
  { name: 'Launch Requests', href: '/launch-requests', icon: Rocket },
  { name: 'Starred', href: '/starred', icon: Star },
  { name: 'Recents', href: '/recents', icon: Clock },
  { name: 'Shared with me', href: '/shared-with-me', icon: UserCheck },
  { name: 'Trash', href: '/trash', icon: Trash2 },
  { name: 'Shared by You', href: '/shared-by-me', icon: Share2 },
  { name: 'Teams', href: '/teams', icon: Users },
  { name: 'Settings', href: '/settings', icon: User },
  { name: 'FAQ / Help', href: '/faq', icon: HelpCircle },
];

// Navigation items for admin and buyers only (not for creatives/editors)
const adminBuyerNavigation = [
  { name: 'Access Requests', href: '/access-requests', icon: Key },
  { name: 'Testing Tracker', href: '/testing-tracker', icon: FlaskConical },
];

// Admin-only navigation items
const adminOnlyNavigation = [
  { name: 'Analytics Engine', href: '/analytics-engine', icon: Cpu },
  { name: 'Alerts Dashboard', href: '/alerts-dashboard', icon: BellRing },
  { name: 'Editors', href: '/editors', icon: Users },
  { name: 'Workload', href: '/workload', icon: BarChart3 },
  { name: 'Tracking Report', href: '/tracking-report', icon: CalendarCheck },
  { name: 'Creative Usage', href: '/creative-usage', icon: Target },
  { name: 'Metadata Extraction', href: '/metadata', icon: Tags },
];

// Editor (creative) specific navigation items
const editorNavigation = [
  { name: 'Analytics Engine', href: '/analytics-engine', icon: Cpu },
  { name: 'Workload', href: '/workload', icon: BarChart3 },
  { name: 'Tracking Report', href: '/tracking-report', icon: CalendarCheck },
];

const adminNavigation = [
  { name: 'Admin Panel', href: '/admin', icon: Settings },
  { name: 'RBAC Permissions', href: '/rbac-admin', icon: Shield },
  { name: 'Activity Logs', href: '/activity-logs', icon: FileText },
  { name: 'Log Exports', href: '/activity-log-export', icon: Download },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, featureFlags } = useAuth();
  // Collapsible sidebar — persisted so it stays how the user left it. Collapsed = icon-only rail.
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem('sidebar_collapsed') === '1');
  const toggleCollapsed = () => setCollapsed(c => { const n = !c; localStorage.setItem('sidebar_collapsed', n ? '1' : '0'); return n; });
  const [favoriteCollections, setFavoriteCollections] = useState<any[]>([]);
  const [pendingCounts, setPendingCounts] = useState({ fileRequests: 0, launchRequests: 0, testingTracker: 0 });

  // Build navigation based on user role (check additional_roles too)
  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const allUserRolesNav: string[] = [user?.role, ...additionalRoles].filter(Boolean) as string[];

  // Roles that get full admin-like nav (Analytics Engine, Testing Tracker, Alerts Dashboard, etc.)
  const ELEVATED_ROLES = ['admin', 'team_lead', 'ceo', 'head_media_buying', 'creative_head', 'assistant_team_lead'];
  const isAdminLike = allUserRolesNav.some(r => ELEVATED_ROLES.includes(r));

  const isATL = user?.role === 'assistant_team_lead' ||
    additionalRoles.includes('assistant_team_lead');
  const isVH = additionalRoles.includes('vertical_head') ||
    ((user as any)?.assigned_verticals?.length > 0);

  const navigation = (() => {
    if (isAdminLike) {
      return [...baseNavigation, ...adminBuyerNavigation, ...adminOnlyNavigation];
    } else if (isATL || isVH) {
      // ATLs and VHs get base nav + analytics + workload + access requests
      return [...baseNavigation, ...adminBuyerNavigation, ...editorNavigation];
    } else if (user?.role === 'creative') {
      // Editors get base navigation + their specific items (Analytics, Workload)
      return [...baseNavigation, ...editorNavigation];
    } else if (user?.role === 'buyer') {
      // Buyers get base navigation + Access Requests
      return [...baseNavigation, ...adminBuyerNavigation];
    }
    // Default: base navigation only
    return baseNavigation;
  })();

  // Script Tracker: only for specific users
  const SCRIPT_TRACKER_EMAILS = ['parmeet.singh@pearmediallc.com', 'developer@pearmediallc.com'];
  const withScriptTracker = SCRIPT_TRACKER_EMAILS.includes(user?.email || '')
    ? [...navigation, { name: 'Script Tracker', href: '/script-tracker', icon: ClipboardList }]
    : navigation;

  // Quality Check: only for admin, creative_head, quality_check roles
  const canSeeQC = allUserRolesNav.some(r => ['admin', 'creative_head', 'quality_check', 'ceo', 'head_media_buying'].includes(r || ''));
  const withQC = canSeeQC
    ? [...withScriptTracker, { name: 'Quality Check', href: '/quality-check', icon: CheckSquare }]
    : withScriptTracker;

  // Script Database: visible to all authenticated users
  const withScriptDb = [...withQC, { name: 'Script Database', href: '/script-database', icon: BookOpen }];

  // Winners Library: admin/ceo only (mirrors backend requireSuperAdmin on /api/winners).
  const isSuperAdmin = allUserRolesNav.some(r => ['admin', 'ceo'].includes(r || ''));
  const withWinners = isSuperAdmin
    ? [...withScriptDb, { name: 'Winners Library', href: '/winners-library', icon: Trophy }]
    : withScriptDb;

  // Campaign Lookup: only when download_codes feature is enabled, visible to buyers + admins
  const canSeeCampaignLookup = featureFlags?.download_codes_enabled &&
    allUserRolesNav.some(r => ['buyer', 'admin', 'team_lead', 'ceo', 'head_media_buying', 'creative_head'].includes(r || ''));
  const baseFinalNavigation = canSeeCampaignLookup
    ? [...withWinners, { name: 'Campaign Lookup', href: '/campaign-lookup', icon: Search }]
    : withWinners;

  // Variation Studio: the email allowlist (REGEN_ADMIN_EMAILS in lib/utils) OR a per-user
  // 'variation_studio' additional_role granted from the admin panel — MUST mirror the backend
  // canUseStudio gate, or a role-granted user gets access on the API but never sees the nav link.
  const _canUseStudio = isRegenAdmin(user?.email)
    || (Array.isArray((user as any)?.additional_roles) && (user as any).additional_roles.includes('variation_studio'));
  const finalNavigation = _canUseStudio
    ? [...baseFinalNavigation, { name: 'Variation Studio', href: '/variation-studio', icon: Wand2 }]
    : baseFinalNavigation;

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  // Fetch favorite collections
  useEffect(() => {
    const fetchFavoriteCollections = async () => {
      try {
        const response = await savedSearchApi.getAll();
        const collections = response.data.data || [];
        const favorites = collections.filter((c: any) => c.is_favorite).slice(0, 5);
        setFavoriteCollections(favorites);
      } catch (error) {
        console.error('Failed to fetch favorite collections:', error);
      }
    };

    if (user) {
      fetchFavoriteCollections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Poll pending counts for sidebar badges
  useEffect(() => {
    if (!user) return;

    const fetchPendingCounts = async () => {
      try {
        const response = await notificationApi.getPendingCounts();
        if (response.data?.data) {
          setPendingCounts(response.data.data);
        }
      } catch (error) {
        // Silently fail - badge counts are non-critical
      }
    };

    fetchPendingCounts();
    const interval = setInterval(() => {
      if (!document.hidden) fetchPendingCounts();
    }, 120000); // refresh every 2 min, skip when tab hidden
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleCollectionClick = (collection: any) => {
    const filters = collection.filters;
    const params = new URLSearchParams();

    if (filters.search_term) params.set('search', filters.search_term);
    if (filters.media_types?.length) params.set('media_type', filters.media_types.join(','));
    if (filters.editor_ids?.length) params.set('editor_id', filters.editor_ids.join(','));
    if (filters.buyer_ids?.length) params.set('buyer_id', filters.buyer_ids.join(','));
    if (filters.folder_ids?.length) params.set('folder_id', filters.folder_ids.join(','));
    if (filters.tags?.length) params.set('tags', filters.tags.join(','));
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);

    navigate(`/media?${params.toString()}&collection=${collection.id}&collection_name=${encodeURIComponent(collection.name)}`);
  };

  return (
    <div className={cn('flex h-full flex-col bg-sidebar border-r border-sidebar-border transition-[width] duration-200',
                       collapsed ? 'w-16' : 'w-64')}>
      <div className={cn('flex h-16 items-center border-b border-sidebar-border',
                         collapsed ? 'justify-center px-1' : 'justify-between px-3')}>
        {!collapsed && <img src="/logo.png" alt="Orbit by Pear Media" className="object-contain" style={{ height: '50px' }} />}
        <button onClick={toggleCollapsed} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-2 rounded-lg text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors">
          {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto space-y-1 px-3 py-4">
        {finalNavigation.map((item) => {
          const Icon = item.icon;
          // Determine badge count for this nav item
          const badgeCount =
            item.href === '/file-requests' ? pendingCounts.fileRequests :
            item.href === '/launch-requests' ? pendingCounts.launchRequests :
            item.href === '/testing-tracker' ? pendingCounts.testingTracker :
            0;

          return (
            <Link
              key={item.name}
              to={item.href}
              title={collapsed ? item.name : undefined}
              className={cn(
                'relative flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-colors',
                collapsed ? 'justify-center px-2' : 'px-3',
                isActive(item.href)
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
              )}
            >
              <Icon size={18} />
              {!collapsed && <span className="flex-1">{item.name}</span>}
              {badgeCount > 0 && (
                <span className={cn('inline-flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full',
                  collapsed ? 'absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1' : 'min-w-[20px] h-5 px-1.5')}>
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </Link>
          );
        })}

        {/* Collections Section — hidden in the collapsed icon-rail */}
        {!collapsed && <>
        <div className="h-px bg-sidebar-border my-4" />
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase">Collections</h3>
            <Link
              to="/collections"
              className="text-xs text-muted-foreground hover:text-sidebar-foreground"
            >
              View All
            </Link>
          </div>
          {favoriteCollections.length > 0 ? (
            <div className="space-y-1">
              {favoriteCollections.map((collection) => (
                <button
                  key={collection.id}
                  onClick={() => handleCollectionClick(collection)}
                  className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors text-left"
                >
                  <Layers size={16} style={{ color: collection.color || '#3B82F6' }} />
                  <span className="truncate flex-1">{collection.name}</span>
                  <ChevronRight size={14} className="text-muted-foreground" />
                </button>
              ))}
            </div>
          ) : (
            <Link
              to="/collections"
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-sidebar-accent/50 transition-colors"
            >
              <Layers size={16} />
              <span>Create Collection</span>
            </Link>
          )}
        </div>
        </>}

        {user?.role === 'admin' && (
          <>
            <div className="h-px bg-sidebar-border my-4" />
            {adminNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  title={collapsed ? item.name : undefined}
                  className={cn(
                    'flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-colors',
                    collapsed ? 'justify-center px-2' : 'px-3',
                    isActive(item.href)
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
                  )}
                >
                  <Icon size={18} />
                  {!collapsed && item.name}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      <div className={cn('border-t border-sidebar-border', collapsed ? 'p-2' : 'p-4')}>
        <div className={cn('flex items-center', collapsed ? 'flex-col gap-2' : 'justify-between')}>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-sidebar-accent flex items-center justify-center text-sm font-medium"
                 title={collapsed ? user?.name : undefined}>
              {user?.name.charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex flex-col">
                <p className="text-sm font-medium text-sidebar-foreground">{user?.name}</p>
                <p className="text-xs text-muted-foreground">
                  {[user?.role, ...additionalRoles].filter((r, i, arr) => r && arr.indexOf(r) === i).map(r => r!.replace(/_/g, ' ')).join(' + ')}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            className="text-sm text-muted-foreground hover:text-sidebar-foreground"
            title="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
